/* Settings page - Notification & Email configuration. Admin only. */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Plus, Trash2, Clock, Send, CheckCircle2, AlertCircle, Brain } from 'lucide-react';

const BAR_OPTIONS = [
    { value: 'lobby', label: 'Lobby Bar' },
    { value: 'servis', label: 'Servis Bar' },
    { value: 'vitamin', label: 'Vitamin Bar' },
    { value: 'havuz', label: 'Havuz Bar' },
    { value: 'plaj', label: 'Plaj Bar' },
    { value: 'amfi', label: 'Amfi Bar' },
    { value: 'teras', label: 'Teras Bar' },
    { value: 'alacarte', label: 'Alacarte Bar' },
    { value: 'minibar', label: 'Minibar' },
];

export default function Settings() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [newRecipient, setNewRecipient] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/notification-settings');
            setSettings(res.data);
        } catch (e) {
            toast.error('Ayarlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const res = await api.put('/notification-settings', {
                enabled: settings.enabled,
                recipients: settings.recipients,
                schedule_hour: settings.schedule_hour,
                include_bars: settings.include_bars,
            });
            setSettings(res.data);
            toast.success('Ayarlar kaydedildi');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    const testSend = async () => {
        setTesting(true);
        try {
            const res = await api.post('/notification-settings/test-send');
            toast.success(res.data.message || 'Gönderim arka planda başlatıldı');
            // Refresh after 90s to catch status update
            setTimeout(() => load(), 90000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Test gönderimi başlatılamadı');
        } finally {
            setTesting(false);
        }
    };

    const addRecipient = () => {
        const email = newRecipient.trim();
        if (!email || !email.includes('@')) {
            toast.error('Geçerli bir e-posta girin');
            return;
        }
        if (settings.recipients.includes(email)) {
            toast.error('Bu e-posta zaten ekli');
            return;
        }
        setSettings({ ...settings, recipients: [...settings.recipients, email] });
        setNewRecipient('');
    };

    const removeRecipient = (email) => {
        setSettings({ ...settings, recipients: settings.recipients.filter(r => r !== email) });
    };

    const toggleBar = (barId) => {
        const current = new Set(settings.include_bars);
        if (current.has(barId)) current.delete(barId);
        else current.add(barId);
        setSettings({ ...settings, include_bars: Array.from(current) });
    };

    if (loading || !settings) {
        return <div className="p-8 text-center text-slate-400">Yükleniyor...</div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                </Button>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Ayarlar</h1>
            </div>

            <Card className="border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                    <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        Yapay Zekâ E-posta Bildirimleri
                    </CardTitle>
                    <CardDescription>
                        Her sabah Claude Sonnet 4.5 tüm barların 14 günlük verisini inceler ve günlük talep planını e-posta ile gönderir
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 pt-4">
                    {/* Enable toggle */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                            <Label className="text-sm font-semibold">Otomatik Gönderim</Label>
                            <p className="text-xs text-slate-500">Her sabah belirtilen saatte otomatik çalışır</p>
                        </div>
                        <Switch
                            checked={settings.enabled}
                            onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                            data-testid="enable-toggle"
                        />
                    </div>

                    {/* Schedule hour */}
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-1">
                            <Clock className="w-4 h-4" /> Gönderim Saati (Türkiye)
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                type="number"
                                min="0"
                                max="23"
                                value={settings.schedule_hour}
                                onChange={(e) => setSettings({ ...settings, schedule_hour: parseInt(e.target.value, 10) || 0 })}
                                className="w-24"
                                data-testid="schedule-hour"
                            />
                            <span className="text-sm text-slate-500">:00 - örn. 7 = sabah 07:00</span>
                        </div>
                    </div>

                    {/* Recipients */}
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-1">
                            <Mail className="w-4 h-4" /> Alıcı E-postalar ({settings.recipients.length})
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                type="email"
                                placeholder="ornek@grandkaptanhotel.com"
                                value={newRecipient}
                                onChange={(e) => setNewRecipient(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                                data-testid="new-recipient-input"
                            />
                            <Button onClick={addRecipient} data-testid="add-recipient-btn">
                                <Plus className="w-4 h-4 mr-1" /> Ekle
                            </Button>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {settings.recipients.length === 0 ? (
                                <p className="text-xs text-slate-400">Henüz alıcı eklenmedi</p>
                            ) : (
                                settings.recipients.map(email => (
                                    <Badge key={email} variant="outline" className="flex items-center gap-1 py-1 px-2">
                                        {email}
                                        <button onClick={() => removeRecipient(email)} className="ml-1 text-red-500 hover:text-red-700" data-testid={`remove-recipient-${email}`}>
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Bars filter */}
                    <div>
                        <Label className="text-sm font-medium">Dahil Edilecek Barlar</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                            {BAR_OPTIONS.map(bar => (
                                <label key={bar.value} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                                    <input
                                        type="checkbox"
                                        checked={settings.include_bars.includes(bar.value)}
                                        onChange={() => toggleBar(bar.value)}
                                        data-testid={`bar-toggle-${bar.value}`}
                                    />
                                    <span className="text-sm">{bar.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Last status */}
                    {settings.last_sent_at && (
                        <div className="p-3 bg-slate-50 rounded-lg border">
                            <div className="flex items-center gap-2 text-sm">
                                {settings.last_status === 'OK' ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                ) : (
                                    <AlertCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span className="text-slate-700">Son gönderim: <b>{new Date(settings.last_sent_at).toLocaleString('tr-TR')}</b></span>
                            </div>
                            {settings.last_status !== 'OK' && (
                                <p className="text-xs text-red-600 mt-1">{settings.last_status}</p>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t">
                        <Button onClick={save} disabled={saving} className="bg-slate-800 hover:bg-slate-900" data-testid="save-settings-btn">
                            {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                        </Button>
                        <Button
                            onClick={testSend}
                            disabled={testing || settings.recipients.length === 0}
                            variant="outline"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid="test-send-btn"
                        >
                            <Send className="w-4 h-4 mr-1" />
                            {testing ? 'Gönderiliyor (30-60 sn)...' : 'Şimdi Test Gönder'}
                        </Button>
                    </div>

                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                        <b>Not:</b> SMTP_PASSWORD ayarlanmamışsa gönderim başarısız olur. Yöneticiniz <code>admin@grandkaptanhotel.com</code> Outlook hesabı için bir uygulama parolası ekleyene kadar bu özellik beklemede kalır.
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
